/** Reusable button component with variant and size support. */
'use client';

// Admin's button dialect — a skin over the shared @auctogon/ui Button (BL-1711,
// ADR 030 § 3b Canon merge: bidder's behaviour model, admin's variant
// vocabulary).
//
// Behaviour — loading, disabled, the `type="button"` default (BUG-1390),
// `aria-busy` — lives in the shared core. This file binds admin's look and
// nothing else: the class strings below are the pre-merge ones verbatim, so no
// admin button changes appearance. The default export and `ButtonProps` are
// unchanged, so every call site migrates with no edit (RAC 4).
//
// BL-1698 adds the settings-v2 dialect: `dialect="settings"` renders the
// stylesheet's own `ui-btn` classes, so a hand-written settings button can move
// onto this component without changing its look. The mapping:
//   primary → ui-btn--primary · secondary → --secondary · ghost → --ghost
//   destructive → --danger · text → --text
//   sm → ui-btn--sm · md → (none) · lg → ui-btn--lg · icon → the ui-icon-btn box
// `text` (ui-btn--text) is a variant the classic dialect has no twin for, so the
// type offers it only under `dialect="settings"` (AC 2). `.ui-btn` in the
// stylesheet owns hover, pressed, :disabled and the phone-sheet red, so this
// dialect carries no disabled class, merge or sheet hook. The `.ui-icon-btn` box
// styles only hover (and danger hover), exactly as the hand-written markup did.

import { forwardRef } from 'react';
import { Button as UiButton, type ButtonDialect } from '@auctogon/ui/web';
import { cn } from './tw-merge';
import { useModalPresentation } from './Modal';

/** Props both dialects share. */
interface ButtonCommonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button size */
  size?: 'sm' | 'md' | 'lg' | 'icon';
  /** Show loading spinner and disable interaction */
  isLoading?: boolean;
  children: React.ReactNode;
}

/**
 * Props for the classic admin look (Tailwind paint), the default dialect.
 * Kept as `ButtonProps` so its variant list stays the classic one.
 * @extends React.ButtonHTMLAttributes<HTMLButtonElement>
 */
export interface ButtonProps extends ButtonCommonProps {
  /** Which look: `classic` (default) or `settings` (the settings-v2 `ui-btn` classes). */
  dialect?: 'classic';
  /**
   * Visual style variant.
   *
   * Destructive comes in two weights, and picking the right one matters (BL-1075):
   * - `destructive` — solid red, for the **commit point**: the confirming button
   *   inside a `ConfirmDialog` or a bespoke confirm modal, where the operator has
   *   already chosen to proceed and full weight is a useful last warning.
   * - `destructive-subtle` — tonal red, for a destructive **entry point** resting
   *   on a page ("Delete Auction", a header/row action). Reads as dangerous
   *   without out-shouting the ordinary actions beside it.
   *
   * Two variants exist for buttons sitting ON a dark brand surface — today the
   * clerk console's command bar (BL-1254), where `secondary`'s mint fill and
   * `ghost`'s grey text both disappear against `--primary`:
   * - `ondark` — translucent white on the dark bar, the neutral action.
   * - `ondark-warning` — amber, for Pause/Resume. Outlined while the sale runs,
   *   solid once it is paused, so a paused auction is unmissable from across a
   *   sale room rather than a state you have to read for.
   */
  variant?:
    | 'primary'
    | 'secondary'
    | 'destructive'
    | 'destructive-subtle'
    | 'warning'
    | 'ghost'
    | 'outline'
    | 'ondark'
    | 'ondark-warning'
    | 'ondark-warning-active';
}

/** Props for the settings-v2 look (BL-1698): the stylesheet's `ui-btn` classes. */
export interface SettingsButtonProps extends ButtonCommonProps {
  dialect: 'settings';
  /** Visual style variant. `text` is the settings-only link-weight button. */
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'text';
}

type ClassicVariant = NonNullable<ButtonProps['variant']>;
type SettingsVariant = NonNullable<SettingsButtonProps['variant']>;
type ButtonSize = NonNullable<ButtonCommonProps['size']>;

// BUG-1534: each variant answers a press with a SECOND signal beside the base's
// `enabled:active:scale-[0.97]` — the scale alone did not read on a filled button (the
// outline Cancel visibly shrank; the primary Add Customer beside it seemed dead).
// Pressed = one step darker on the variant's own ramp; on the dark console rail,
// one step brighter. Signed off by Vadim, 2026-09-29. Measured (ADR 051):
//   #fff on teal-900 #073b38 .............. 12.41:1  primary
//   teal-900 on #3bc474 ................... 5.52:1   secondary (text darkens too:
//                                                    teal-800 on it is 4.07)
//   #fff on danger-700 #b91c1c ............ 6.47:1   destructive
//   danger-700 on danger-100 #faeeeb ...... 5.70:1   destructive-subtle (BL-2936)
//   #fff on amber-700 #b45309 ............. 5.02:1   warning
//   gray-700 on gray-200 #e5e7eb .......... 8.33:1   ghost, outline
//   #fff on white/25 over teal-800 ........ 4.75:1   ondark
//   amber-100 on amber-400/25 over teal-800  5.41:1  ondark-warning
//   amber-950 on amber-500 #f59e0b ........ 6.97:1   ondark-warning-active
// The settings-v2 vars carry a hex fallback: that stylesheet is route-scoped, and
// an undefined var would make the pressed fill transparent, not merely unchanged.
// Every pressed utility is `enabled:active:`, never bare `active:` — see the
// base classes below. A caller overriding the pressed fill writes the same
// `enabled:active:bg-…` so `cn` replaces the variant's; a bare `active:bg-…`
// would leave both in the DOM and lose to the guarded one's higher specificity.
const variantClasses: Record<ClassicVariant, string> = {
  primary:
    'bg-primary text-white hover:bg-primary-hover enabled:active:bg-[var(--teal-900,#073b38)] focus-visible:ring-secondary',
  secondary:
    'bg-secondary text-primary hover:bg-[#3bc474] enabled:active:bg-[#3bc474] enabled:active:text-[var(--teal-900,#073b38)] focus-visible:ring-secondary',
  // BL-1258: the danger fill is the settings-v2 `--danger-*` scale, NOT
  // Tailwind's red. The two disagreed (#dc2626 here vs #A8331B in
  // `.ui-btn--danger`), so a destructive confirm looked like a different
  // kind of warning depending on which button implementation drew it.
  // BL-2936 moved the scale itself to #DC2626 / #B91C1C, so they match again
  // through the token.
  destructive:
    'bg-[var(--danger-500)] text-white hover:bg-[var(--danger-700)] enabled:active:bg-[var(--danger-700,#b91c1c)] focus-visible:ring-[var(--danger-500)]',
  // Tonal pair, mirroring `.ui-alert--danger`'s ratified vocabulary
  // (bg-50 / text-700 / border-100) rather than inventing palette steps.
  'destructive-subtle':
    'border border-[var(--danger-100)] bg-[var(--danger-50)] text-[var(--danger-700)] hover:bg-[var(--danger-100)] enabled:active:bg-[var(--danger-100,#faeeeb)] focus-visible:ring-[var(--danger-500)]',
  warning:
    'bg-amber-600 text-white hover:bg-amber-700 enabled:active:bg-amber-700 focus-visible:ring-amber-500',
  ghost:
    'bg-transparent text-gray-700 hover:bg-gray-100 enabled:active:bg-gray-200 focus-visible:ring-gray-300',
  outline:
    'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 enabled:active:bg-gray-200 focus-visible:ring-secondary',
  ondark:
    'bg-white/10 text-white border border-white/25 hover:bg-white/20 enabled:active:bg-white/25 focus-visible:ring-white/50',
  'ondark-warning':
    'bg-amber-400/15 text-amber-300 border border-amber-400/55 hover:bg-amber-400/25 enabled:active:bg-amber-400/25 enabled:active:text-amber-100 focus-visible:ring-amber-300',
  'ondark-warning-active':
    'bg-amber-400 text-amber-900 border border-transparent hover:bg-amber-300 enabled:active:bg-amber-500 enabled:active:text-amber-950 focus-visible:ring-amber-200',
};

/**
 * The phone sheet's red for an action that cannot be undone (ADR 063 — Type
 * Scale Amendment 5, BL-2403): #DC2626, #B91C1C while pressed. Only inside a
 * sheet, so a `ConfirmDialog` and every desktop button keep the settings-v2
 * danger scale above.
 *   #fff on red-600 #dc2626 ............... 4.83:1   destructive (sheet)
 *   #fff on red-700 #b91c1c ............... 6.47:1   destructive pressed (sheet)
 */
const SHEET_DESTRUCTIVE =
  'bg-red-600 text-white hover:bg-red-700 enabled:active:bg-red-700 focus-visible:ring-red-600';

// BL-2546: on the phone sheet the secondary (outline) button is one look: a
// teal tint, no outline, 10px corners, 44px tall, and a press that moves it
// down 1px instead of shrinking it.
//   --primary #09514d on the tint over white (#ebf1f1) ... 8.01:1  text ✓ (needs 4.5)
//   --primary on the hover/pressed tint over white (#e1eaea) ... 7.47:1  text ✓
//   --primary on the tint over gray-50, VIN panel (#e6eced) ..... 7.66:1  text ✓
//   --primary on the pressed tint over gray-50 (#dce6e6) ........ 7.19:1  text ✓
// The tint is an opacity wash of the brand teal: the `primary` token is a bare
// var() with no <alpha-value> channel, so `bg-primary/[0.08]` renders nothing
// (design-system-enforcement.md escape hatch).
export const SHEET_SECONDARY_CLASSES =
  // eslint-disable-next-line brandhex/no-hardcoded-brand-hex -- opacity wash, see above
  'border-0 bg-[#09514d]/[0.08] text-primary hover:bg-[#09514d]/[0.12] enabled:active:bg-[#09514d]/[0.12] rounded-[10px] min-h-11 enabled:active:scale-100 enabled:active:translate-y-px focus-visible:ring-secondary';

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'px-3 py-1.5 text-caption',
  md: 'px-4 py-2 text-body',
  lg: 'px-6 py-3 text-body-lg',
  icon: 'p-2',
};

/**
 * Inside a phone sheet the destructive fill swaps to the sheet red (BL-2403),
 * and the outline button becomes the sheet's tinted secondary (BL-2546).
 * A hook, so the shared core asks for it on every render without knowing what
 * a sheet is.
 *
 * @param variant - The variant being rendered.
 * @returns The sheet look for `destructive` or `outline` inside a sheet, else undefined.
 */
function useAdminVariantClass(variant: ClassicVariant): string | undefined {
  if (useModalPresentation() !== 'sheet') return undefined;
  if (variant === 'destructive') return SHEET_DESTRUCTIVE;
  if (variant === 'outline') return SHEET_SECONDARY_CLASSES;
  return undefined;
}

// The base classes, shared by every variant.
//
// `focus-visible:`, not `focus:` — the ring is for someone arriving
// by keyboard, and `focus:` cannot tell that from a mouse click or a
// finger. On a phone it lit on every tap and then STAYED lit, because
// focus has nowhere else to go: the hamburger wore a bright box for
// the rest of the session. The native outline is still suppressed
// unconditionally, so nothing double-draws.
//
// `ring-offset-1` went with it. Its colour defaults to WHITE, so on
// the dark teal rail the "gap" was itself a bright line and the ring
// read as a heavy double contour. Fixing the colour per dark variant
// would have been a third pattern; `PanelHeader` — the close control
// on what was then the same teal header — already solved this by dropping the
// offset and is the shape copied here.
// `enabled:active:scale-[0.97]` — the pressed state. Until this, the button
// had ten `hover:` rules and NOT ONE `active:`, which meant a phone
// got no press feedback at all: `hover:` does not fire on a press,
// because a finger touches rather than points. An operator tapped
// Save and the button did nothing until the request came back — and
// on a slow one, tapped it again.
//
// Copied from bidder-web's `BidderButton`, values included, so the
// two apps' buttons answer a finger the same way. `transition-colors`
// widened to name `transform` as well, or the scale would snap; the
// shorter 100ms is the press-feedback timing, not the 150ms colour
// default. `motion-reduce:transition-none` because someone who asked
// their OS for less motion gets the state change without the
// animation, not the animation without a choice.
//
// `enabled:` because browsers DO fire `:active` on a disabled button
// (Chrome, real mouse-down): bare `active:` shrank and darkened the
// disabled Internal notes "Add" (BUG-1534 AC 4). The guard is CSS,
// not a JS check of `isDisabled`, so a Button disabled by its
// `<fieldset disabled>` (auction settings, ItemForm) holds still too.
// Same guard as `.ui-btn:active:not(:disabled)` in settings-v2.css.
const ADMIN_BUTTON_DIALECT: ButtonDialect<ClassicVariant, ButtonSize> = {
  base: 'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-[transform,background-color,border-color,color] duration-100 focus:outline-none enabled:active:scale-[0.97] focus-visible:ring-2 motion-reduce:transition-none',
  variants: variantClasses,
  sizes: sizeClasses,
  disabled: 'opacity-50 cursor-not-allowed',
  // The label stays beside the spinner, except on an icon-only button, which
  // has no room for both.
  hideLabelWhileLoading: ['icon'],
  // `cn` resolves conflicting Tailwind utilities so the LAST one wins: the
  // caller's `className` beats the variant/size defaults (BL-1004). Not bare
  // `twMerge`: it reads the type-scale steps as colours and would drop a
  // variant's `text-white` (BL-1841); `./tw-merge` teaches it the scale.
  merge: cn,
  useVariantClass: useAdminVariantClass,
};

// The settings-v2 look (BL-1698). Class names only: `.ui-btn` in
// settings-v2.css owns hover, pressed, focus and `:disabled`.
const SETTINGS_BUTTON_DIALECT: ButtonDialect<SettingsVariant, Exclude<ButtonSize, 'icon'>> = {
  base: 'ui-btn',
  variants: {
    primary: 'ui-btn--primary',
    secondary: 'ui-btn--secondary',
    ghost: 'ui-btn--ghost',
    destructive: 'ui-btn--danger',
    text: 'ui-btn--text',
  },
  sizes: { sm: 'ui-btn--sm', md: '', lg: 'ui-btn--lg' },
};

// `.ui-icon-btn` is its own box, not a `.ui-btn` size, so an icon-only settings
// button binds this dialect instead. Only `destructive` has a modifier of its
// own; the other variants render the plain icon box.
const SETTINGS_ICON_BUTTON_DIALECT: ButtonDialect<SettingsVariant, 'icon'> = {
  base: 'ui-icon-btn',
  variants: {
    primary: '',
    secondary: '',
    ghost: '',
    destructive: 'ui-icon-btn--danger',
    text: '',
  },
  sizes: { icon: '' },
  hideLabelWhileLoading: ['icon'],
};

/**
 * Reusable button with variant, size, and loading support.
 * @param props - Button props including dialect, variant, size, isLoading, and standard button attributes.
 * @returns A styled button element.
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps | SettingsButtonProps>((props, ref) => {
  if (props.dialect === 'settings') {
    const { dialect: _dialect, variant = 'primary', size = 'md', ...rest } = props;
    return size === 'icon' ? (
      <UiButton ref={ref} dialect={SETTINGS_ICON_BUTTON_DIALECT} variant={variant} size="icon" {...rest} />
    ) : (
      <UiButton ref={ref} dialect={SETTINGS_BUTTON_DIALECT} variant={variant} size={size} {...rest} />
    );
  }
  const { dialect: _dialect, variant = 'primary', size = 'md', ...rest } = props;
  return <UiButton ref={ref} dialect={ADMIN_BUTTON_DIALECT} variant={variant} size={size} {...rest} />;
});

Button.displayName = 'Button';

export default Button;
